import { AxiosError, isAxiosError } from 'axios';

import {
  NETWORK_ERROR,
  NETWORK_ERROR_CODE,
  STORAGE_KEY,
  TIMEOUT,
} from '@src/constants';
import { timeout } from '@src/helpers/timeout.helpers';
import { IFetchWithInterval } from '@src/interfaces/axios.interfaces';

export const getToken = () => localStorage.getItem(STORAGE_KEY.TOKEN);

export const setToken = (token: string) =>
  localStorage.setItem(STORAGE_KEY.TOKEN, token);

export const removeToken = () => localStorage.removeItem(STORAGE_KEY.TOKEN);

export const transformErrorMessage = (error: AxiosError) => {
  const responseData = error.response?.data as
    string | { error?: string; message?: string } | undefined;

  if (responseData) {
    if (typeof responseData === 'string') {
      error.message = responseData;
    } else if (typeof responseData === 'object') {
      error.message =
        responseData.error || responseData.message || error.message;
    }
  }
};

export const isErrorException = (error: unknown): error is AxiosError => {
  return [
    isUserNotFoundError(error),
    isUnauthenticatedError(error),
    isEmailAlreadyInUse(error),
    isUserAlreadyExist(error),
    isTokenInvalidError(error),
  ].some(Boolean);
};

export const isEmailAlreadyInUse = (error: unknown): error is AxiosError =>
  isAxiosError(error) &&
  error.response?.status === 400 &&
  error.response.data.error === NETWORK_ERROR.isEmailHasBeenTaken;

export const isUserAlreadyExist = (error: unknown): error is AxiosError =>
  isAxiosError(error) &&
  error.response?.status === 400 &&
  error.response.data.error === NETWORK_ERROR.isUserExist;

export const isUserNotFoundError = (error: unknown): error is AxiosError => {
  return (
    isAxiosError(error) &&
    error.response?.status === 400 &&
    error.response.data.error_code === NETWORK_ERROR_CODE.USER_NOT_FOUND
  );
};

export const isTooManyAttemptsError = (error: unknown): error is AxiosError =>
  isAxiosError(error) &&
  error.response?.data.error_code === NETWORK_ERROR_CODE.TOO_MANY_ATTEMPTS;

export const isTokenInvalidError = (error: unknown): error is AxiosError => {
  return (
    isAxiosError(error) &&
    error.response?.status === 400 &&
    error.response.data.error_code === NETWORK_ERROR_CODE.TOKEN_IS_INVALID
  );
};

export const isUnauthenticatedError = (error: unknown): error is AxiosError =>
  isAxiosError(error) && error.response?.status === 401;

export const fetchWithInterval = async <T>(
  props: IFetchWithInterval<T>,
): Promise<T> => {
  const { seconds = 60, interval = 3, action } = props;

  // Calculate the absolute deadline timestamp
  const deadline = Date.now() + seconds * 1000;

  let lastError: unknown = undefined;

  while (Date.now() <= deadline) {
    const controller = new AbortController();

    try {
      const result = await action(controller.signal);

      // IMPORTANT:
      // false / 0 / '' are valid results and must NOT be treated as "no result"
      if (result !== undefined) {
        return result;
      }
    } catch (error) {
      // Store the last error but do not fail immediately
      lastError = error;
    } finally {
      controller.abort();
    }

    // Wait before the next attempt if we still have time
    if (Date.now() + interval * 1000 > deadline) break;
    await timeout(interval * TIMEOUT.ONE_SECOND);
  }

  // If no valid result was returned during the whole period
  if (lastError) {
    throw lastError;
  }

  throw new Error('fetchWithInterval: no result before timeout');
};
